// Do not use prefix slash because ky will add it automatically
export const ENDPOINTS = {
  AUTH: {
    REGISTER_EMAIL: "user/signup",
    REGISTER_EMAIL_VERIFY: "user/signup-verify",
    LOGIN_EMAIL: "auth/signin",
    LOGIN_EMAIL_VERIFY: "auth/signin-verify",
    LOGIN_GUEST: "auth/guest/signin",
    REFRESH_TOKEN: "auth/refresh-token",
    LOGOUT: "auth/logout",
    FORGOT_PASSWORD: "auth/forget-password",
    RESET_PASSWORD: "auth/reset-password",
    SEND_OTP: "otp/send"
  },
  PROFILE: {
    USER_DATA: "user/detail",
    CHANGE_PASSWORD: "user/update-password",
    CHANGE_PASSWORD_WITH_OTP: "user/send-otp-reset-password",
    UPDATE_PROFILE: "user",
    SEND_OTP_WITHOUT_AUTH: "user/send-otp"
  },
  ADDRESS: {
    COUNTRIES: "address/country",
    CITIES: (countryId: number) => `address/city/${countryId}`
  },
  MEDIA: {
    PRESIGNED_URL: "media/presigned-url",
    UPLOAD: "media/upload"
  },
  PRODUCT: {
    LIST: "product/seller",
    CREATE: "product/seller",
    DETAIL: (productId: string) => `product/seller/${productId}`,
    DELETE: (productId: string) => `product/seller/${productId}`,
    DELETE_PACKAGE: (packageId: number) =>
      `product/seller/package/${packageId}`,
    DELETE_VARIANT: (variantId: number) =>
      `product/seller/variant/${variantId}`,
    VARIANT_LIST: (productPackageId: string) =>
      `product/seller/variant/${productPackageId}`,
    TYPE: "product/type",
    DRAFT: {
      LIST: "product/seller/draft",
      CREATE: "product/seller/draft",
      DETAIL: (productId: string) => `product/seller/draft/${productId}`,
      DELETE: (productId: string) => `product/seller/draft/${productId}`
    },
    FEE_TIER: "product/seller/product_fee_tier",
    AUTOMATION: {
      DETAIL: (productId: string) =>
        `product/seller/product_automation/${productId}`,
      CREATE: "product/seller/product_automation",
      UPDATE: "product/seller/product_automation",
      DEACTIVE: "product/seller/product_automation/deactivation"
    },
    INDUSTRY: {
      LIST: "product/seller/industry-category"
    },
    SET_PUBLISH_STATUS: (productId: string) =>
      `product/seller/publish-status/${productId}`,
    APPROVAL: (productId: string) => `product/seller/approval/${productId}`,
    INTEGRATION: {
      LIST: "product/integration",
      CRM: {
        GET_DETAIL: (productId: string) =>
          `product/integration/crm/${productId}`,
        CREATE: "product/integration/crm",
        UPDATE: (productId: string) => `product/integration/crm/${productId}`,
        DELETE: (productId: string, packageId: number) =>
          `product/integration/crm/${productId}/${packageId}`,
        GET_HIERARCHY: (productId: string) =>
          `product/integration/crm/hierarchy/${productId}`
      },
      BCL: {
        GET_DETAIL: (productId: string) =>
          `product/integration/bcl/${productId}`,
        CREATE: "product/integration/bcl",
        UPDATE: (productId: string) => `product/integration/bcl/${productId}`,
        DELETE: (productId: string) => `product/integration/bcl/${productId}`
      },
      ERFINA: {
        GET_DETAIL: (productId: string) =>
          `product/integration/erfina/${productId}`,
        CREATE: "product/integration/erfina",
        UPDATE: (productId: string) =>
          `product/integration/erfina/${productId}`,
        DELETE: (productId: string) => `product/integration/erfina/${productId}`
      }
    },
    PLATFORM_FEE: "product/seller/package/platform-fee",
    SET_PROMO: (productId: string) =>
      `product/seller/promo-status/${productId}/`
  },
  ORDER: {
    LIST: "order/seller",
    DETAIL: (orderId: string) => `order/seller/${orderId}`,
    COUNT: "order/seller/count_order",
    SEND_ACCESS: (orderItemId: number) =>
      `order/seller/credential/${orderItemId}/submit-credential`,
    RECURRING_ORDERS: "order/seller/order_recurring",
    TRANSACTION_HISTORY: "order/seller/history",
    INTEGRATION_STATUS: (orderId: string) => `integration/status/${orderId}`,
    RETRY_INTEGRATION_STEP: (orderId: string, stepName?: string) =>
      stepName
        ? `integrations/retry/${orderId}/${stepName}`
        : `integrations/retry/${orderId}`,
    READY_TO_CLAIM: "order/seller/ready-to-claim",
    READY_TO_CLAIM_BADGE_COUNTS: "order/seller/ready-to-claim/badge-counts",
    EXPORT: "report/orders/export",
    PAYOUT_STATUS: (orderId: string) => `payment/payout/status/${orderId}`
  },
  ORDER_CLAIMS: {
    LIST: "order-claims",
    DETAIL: (claimId: string) => `order-claims/${claimId}`,
    SUBMIT: "order-claims/seller/submit",
    RESUBMIT: (claimId: string) => `order-claims/seller/submit/${claimId}`,
    DELETE: (claimId: string) => `order-claims/${claimId}`,
    BADGE_COUNTS: "order-claims/seller/badge-counts",
    UPDATE_STATUS: (claimId: string) => `order-claims/${claimId}/status`,
    STATUS_TRACKING: (claimId: string) => `order-claims/${claimId}/histories`,
    ERFINA_STATUS_TRACKING: (publicClaimId: string) =>
      `integration/payment-status/${publicClaimId}`,
    CALCULATE_ESTIMATION: "order-claims/calculate-estimation",
    INTEGRATION_STATUS: (publicId: string) => `integration/status/${publicId}`,
    RETRY_INTEGRATION_STEP: (claimId: string, stepName: string) =>
      `integrations/retry-claim/${claimId}/${stepName}`
  },
  USERS: {
    COUNT: "user/count",
    REGISTER_USER: "users/register",
    RESEND_VERIFICATION: "user/resend-verification",
    BUYER: {
      LIST: "user/superadmin/buyer-list",
      DETAIL: (buyerId: string) => `user/superadmin/${buyerId}/buyer-detail`,
      REQUEST_CA: "user/superadmin/buyer/request-ca",
      PIC_SALES_LIST: (buyerId: string) => `user/pic-sales/${buyerId}`,
      APPROVE: (buyerId: string) =>
        `user/superadmin/role/${buyerId}/guest-to-buyer`,
      REJECT_DOCUMENT: (buyerId: string) =>
        `user/superadmin/${buyerId}/buyer/reject`,
      REJECT_BUYER: (buyerId: string) => `user/superadmin/${buyerId}/buyer`
    },
    SELLER: {
      REGISTER: "user/superadmin/seller",
      LIST: "user/superadmin/seller",
      DETAIL: (sellerId: string) => `user/superadmin/seller/${sellerId}`,
      UPDATE: (sellerId: string) => `user/superadmin/seller/${sellerId}`,
      DELETE: (sellerId: string) => `user/superadmin/seller/${sellerId}`,
      ASSIGN_PASSWORD: "user/seller/assign-password"
    },
    SALES: {
      LIST: "user/superadmin/sales",
      CREATE: "user/superadmin/sales",
      DETAIL: (salesId: string) => `user/superadmin/sales/${salesId}`,
      UPDATE: (salesId: string) => `user/superadmin/sales/${salesId}`,
      DELETE: (salesId: string) => `user/superadmin/sales/${salesId}`
    }
  },

  VOUCHER: {
    LIST: "voucher",
    CREATE: "voucher",
    DETAIL: (voucherId: number) => `voucher/${voucherId}`,
    DELETE: (voucherId: number) => `voucher/${voucherId}`,
    APPROVAL: (voucherId: number) => `voucher/${voucherId}/status`
  },
  GENERATOR: {
    RANDOM_CODE: "generator/random-code"
  },
  INVOICE: {
    REGENERATE: "invoice/regenerate"
  },
  BANK_INFORMATION: {
    MASTER: "bank-master",
    LIST: "bank-account",
    DETAIL: (bankInfoId: string) => `bank-account/${bankInfoId}`,
    CREATE: "bank-account",
    UPDATE: (bankInfoId: string) => `bank-account/${bankInfoId}`,
    DELETE: (bankInfoId: string) => `bank-account/${bankInfoId}`
  },
  BILLING: {
    REPORT_LIST: "billing",
    TAX_LIST: "billing/tax",
    TAX_DETAIL: (payoutId: string) => `billing/tax/${payoutId}`,
    EXPORT: "report/billing/export"
  },
  AUTOMATION: {
    LIST: "automation",
    DETAIL: (id: string) => `automation/${id}`,
    UPDATE: (id: string) => `automation/${id}`,
    SUMMARY: (id: string) => `automation/${id}/summary`,
    DELETE: (id: string) => `automation/${id}`,
    NOTIFY_SETUP: "automation-notification",
    NOTIFY_SETUP_DETAIL: (productId: string) =>
      `automation-notification/${productId}`
  }
};
